import { getFeatureDemandsForProducts } from "library/hubspot/get-products.mjs"

/**
 * Grant-time validation (FIQ-1926).
 *
 * The force option is only usable if you know when you need it, so warning and forcing are one
 * flow: this surfaces the problem while the admin is still choosing demands, and the bypass
 * controls sit directly beneath as the resolution.
 *
 * Without this, a `feature-*` grant that the environment strips would look saved and correct,
 * and the failure would only show up later in the Removed section — after someone had already
 * told the customer it was live.
 */
/**
 * `effectiveDemands` is what the override will actually confer, which is more than what is in
 * `grant.demands` whenever it also names auth bundles — so the caller passes the union of the
 * two. Without that, choosing a bundle full of `feature-*` demands would produce no
 * environment-gate warning at all, which is precisely the case where one is most needed.
 */
export function collectGrantWarnings(grant, effectiveDemands = grant?.demands ?? []) {
    const warnings = []
    const featureDemands = getFeatureDemandsForProducts()
    const selected = effectiveDemands ?? []

    const environmentGated = selected.filter(isEnvironmentGated)
    if (environmentGated.length && !grant.bypass?.serverDemands) {
        warnings.push({
            key: "serverDemands",
            severity: "warning",
            title: "May be stripped by this environment",
            message:
                `${environmentGated.join(", ")} only take effect if the environment's server_demands setting ` +
                `lists them. A real purchase of the product is subject to the same gate. Check the Removed ` +
                `section after saving, or force past it below.`,
        })
    }

    if (grant.scope === "client" && selected.length) {
        warnings.push({
            key: "roles",
            severity: "info",
            title: "Per-user delivery still depends on roles",
            message:
                "A client-level override says the client is entitled to this. Whether a given user gets it " +
                "still depends on their role, which the client administers.",
        })
    }

    return warnings

    function isEnvironmentGated(demand) {
        return featureDemands.has(demand)
    }
}
